import { Row, Col, Typography, Table, Button, message } from "antd";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingPurchaseIndent,
  removeProcessingPurchaseIndent,
  saveProcessingPurchaseIndent,
} from "store/purchase-indent/purchaseIndentSlice";

const MESSAGE_KEY = "SAVE_PURCHASE_INDENT";

const PurchaseIndentSavePage = () => {
  useDocumentTitle("Save Purchase Indent");
  const { processingPurchaseIndent, status, error } = useAppSelector(
    (state) => state.purchaseIndent
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingPurchaseIndent;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingPurchaseIndent());
      }
    }

    if (!Object.keys(processingPurchaseIndent).length) {
      navigate(`/${routes.PURCHASE_INDENT.UPLOAD}`);
    }
  }, [dispatch, processingPurchaseIndent, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Saving purchase indent...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Purchase Indent saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
    } else if (status === "cancelling") {
      message.loading({
        content: "Cancelling purchase indent...",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  const { headerMapping } = processingPurchaseIndent;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSave = () => {
    dispatch(saveProcessingPurchaseIndent());
  };

  const onCancel = () => {
    dispatch(cancelProcessingPurchaseIndent());
  };

  let content;

  if (!Object.keys(processingPurchaseIndent).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingPurchaseIndent.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSave}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancel}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Purchase Indent" />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};

export default PurchaseIndentSavePage;
